import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fmtDT, fmtDate, daysLeft, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProgressBar, StatusBadge, Loading } from "@/components/Shared";
import { toast } from "sonner";
import {
  ArrowLeft, Minus, Plus, Clock, Truck, FileText, Receipt, ShoppingCart,
  Loader2, CheckCircle2, Info, Package, Filter,
} from "lucide-react";

export default function OperationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [op, setOp] = useState(null);
  const [qty, setQty] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [fCat, setFCat] = useState("");
  const [fBrand, setFBrand] = useState("");
  const [fSearch, setFSearch] = useState("");
  const [grossistes, setGrossistes] = useState([]);
  const [grossisteId, setGrossisteId] = useState("");
  const [grossisteCity, setGrossisteCity] = useState("");

  useEffect(() => {
    api.get(`/operations/${id}`).then((r) => setOp(r.data)).catch(() => setOp(false));
    api.get("/grossistes").then((r) => {
      setGrossistes(r.data);
      // Pre-select user's governorate if any grossiste matches
      const myCity = user?.city;
      if (myCity && r.data.some((g) => g.city === myCity)) {
        setGrossisteCity(myCity);
      }
    }).catch(() => {});
  }, [id, user?.city]);

  const setQ = (pid, val, max) => {
    let n = Math.max(0, Math.min(parseInt(val || 0, 10) || 0, max));
    setQty((q) => ({ ...q, [pid]: n }));
  };
  const step = (pid, d, max) => setQty((q) => ({ ...q, [pid]: Math.max(0, Math.min((q[pid] || 0) + d, max)) }));

  const lines = useMemo(() => {
    if (!op?.products) return [];
    return op.products.map((p) => {
      const avail = p.available_qty - (p.sold_qty || 0);
      const q = qty[p.product_id] || 0;
      return { ...p, avail, q, total: q * p.unit_price };
    });
  }, [op, qty]);

  const categories = useMemo(() => [...new Set(lines.map((l) => l.category).filter(Boolean))], [lines]);
  const brands = useMemo(() => [...new Set(lines.map((l) => l.brand).filter(Boolean))].sort(), [lines]);
  const visibleLines = useMemo(() => lines.filter((l) =>
    (!fCat || l.category === fCat) &&
    (!fBrand || l.brand === fBrand) &&
    (!fSearch || l.name.toLowerCase().includes(fSearch.toLowerCase()))
  ), [lines, fCat, fBrand, fSearch]);

  const total = lines.reduce((s, l) => s + l.total, 0);
  const minP = op?.min_per_pharmacy || 0;
  const maxP = op?.max_per_pharmacy || 0;
  const belowMin = minP && total > 0 && total < minP;
  const aboveMax = maxP && total > maxP;
  const canSubmit = total > 0 && !belowMin && !aboveMax && op?.status === "open" && !!grossisteId;

  const submit = async () => {
    if (!grossisteId) { toast.error("Choisissez le grossiste qui assurera la livraison"); return; }
    setSubmitting(true);
    try {
      const items = lines.filter((l) => l.q > 0).map((l) => ({ product_id: l.product_id, qty: l.q }));
      await api.post("/participations", { operation_id: id, grossiste_id: grossisteId, items });
      toast.success("Participation validée avec succès !");
      navigate("/orders");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (op === null) return <Loading />;
  if (op === false) return <div className="text-center py-20 text-slate-500">Opération introuvable. <Link to="/operations" className="text-indigo-600 font-semibold">Retour</Link></div>;

  const dl = daysLeft(op.close_date);
  const isOpen = op.status === "open";

  return (
    <div>
      <Link to="/operations" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-indigo-600 transition-colors duration-200 mb-5"><ArrowLeft className="h-4 w-4" /> Retour au catalogue</Link>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: info + products */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{op.category || "Général"}</span>
                <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-slate-900">{op.name}</h1>
                <div className="text-sm font-semibold text-slate-500 mt-1">{op.supplier_name}</div>
              </div>
              <StatusBadge status={op.status} />
            </div>
            {op.description && <p className="mt-4 text-sm font-medium text-slate-600 leading-relaxed">{op.description}</p>}
            <div className="mt-5"><ProgressBar current={op.committed_amount} target={op.objective_amount} /></div>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                ["Objectif", fmtDT(op.objective_amount)],
                ["Restant", fmtDT(op.remaining_amount)],
                ["Participants", op.participants_count],
                ["Clôture", fmtDate(op.close_date)],
              ].map(([l, v]) => (
                <div key={l}>
                  <div className="text-xs font-semibold text-slate-400 uppercase">{l}</div>
                  <div className="text-sm font-bold text-slate-900 num mt-0.5">{v}</div>
                </div>
              ))}
            </div>
            {isOpen && <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600"><Clock className="h-3.5 w-3.5" />{dl > 0 ? `Clôture dans ${dl} jours` : "Clôture aujourd'hui"}</div>}
          </div>

          {/* Conditions */}
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              [FileText, "Conditions", op.conditions],
              [Truck, "Livraison", op.delivery_conditions],
              [Receipt, "Facturation", op.billing_conditions],
            ].map(([Icon, t, v]) => (
              <div key={t} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="flex items-center gap-2 text-slate-400"><Icon className="h-4 w-4" /><span className="text-xs font-bold uppercase">{t}</span></div>
                <p className="mt-2 text-sm font-medium text-slate-700">{v || "—"}</p>
              </div>
            ))}
          </div>

          {/* Products / cart */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
              <Package className="h-5 w-5 text-indigo-600" />
              <h3 className="font-bold text-slate-900">Composez votre participation</h3>
              <span className="ml-auto text-xs font-semibold text-slate-400 num">{visibleLines.length}/{lines.length} produits</span>
            </div>
            {/* Filters */}
            <div className="px-6 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input data-testid="op-product-search" value={fSearch} onChange={(e) => setFSearch(e.target.value)} placeholder="Filtrer par nom..." className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow" />
              </div>
              <select data-testid="op-filter-category" value={fCat} onChange={(e) => setFCat(e.target.value)} className="h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium bg-white">
                <option value="">Toutes catégories</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select data-testid="op-filter-brand" value={fBrand} onChange={(e) => setFBrand(e.target.value)} className="h-9 px-3 rounded-lg border border-slate-300 text-sm font-medium bg-white">
                <option value="">Toutes marques</option>
                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              {(fCat || fBrand || fSearch) && (
                <button data-testid="op-clear-filters" onClick={() => { setFCat(""); setFBrand(""); setFSearch(""); }} className="h-9 px-3 rounded-lg text-sm font-semibold text-slate-500 hover:bg-slate-100 transition-colors duration-200">Réinitialiser</button>
              )}
            </div>
            <div className="divide-y divide-slate-100">
              {visibleLines.length === 0 && <div className="px-6 py-8 text-center text-sm text-slate-400">Aucun produit ne correspond aux filtres.</div>}
              {visibleLines.map((l) => (
                <div key={l.product_id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`product-row-${l.product_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900">{l.name}</div>
                    <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="num">{fmtDT(l.unit_price)}</span> / unité · <span className="num">{l.avail}</span> dispo.
                      {l.category && <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 font-semibold">{l.category}</span>}
                      {l.brand && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold">{l.brand}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center border border-slate-300 rounded-lg overflow-hidden">
                      <button data-testid={`minus-${l.product_id}`} disabled={!isOpen} onClick={() => step(l.product_id, -1, l.avail)} className="h-10 w-10 grid place-items-center hover:bg-slate-50 text-slate-600 transition-colors duration-200 disabled:opacity-40"><Minus className="h-4 w-4" /></button>
                      <input data-testid={`qty-${l.product_id}`} disabled={!isOpen} value={l.q} onChange={(e) => setQ(l.product_id, e.target.value, l.avail)}
                        className="h-10 w-16 text-center num font-bold text-slate-900 border-x border-slate-200 focus:outline-none focus:bg-indigo-50 disabled:bg-slate-50" />
                      <button data-testid={`plus-${l.product_id}`} disabled={!isOpen} onClick={() => step(l.product_id, 1, l.avail)} className="h-10 w-10 grid place-items-center hover:bg-slate-50 text-slate-600 transition-colors duration-200 disabled:opacity-40"><Plus className="h-4 w-4" /></button>
                    </div>
                    <div className="w-24 text-right num font-bold text-slate-900" data-testid={`line-total-${l.product_id}`}>{fmtDT(l.total)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right: sticky cart summary */}
        <div>
          <div className="sticky top-24 bg-white border border-slate-200 rounded-xl p-6" data-testid="cart-summary">
            <div className="flex items-center gap-2 mb-4"><ShoppingCart className="h-5 w-5 text-indigo-600" /><h3 className="font-bold text-slate-900">Ma participation</h3></div>

            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-500 font-medium">Articles</span><span className="num font-bold text-slate-900">{lines.filter((l) => l.q > 0).length}</span></div>
              <div className="flex justify-between"><span className="text-slate-500 font-medium">Quantité totale</span><span className="num font-bold text-slate-900">{lines.reduce((s, l) => s + l.q, 0)}</span></div>
              {minP > 0 && <div className="flex justify-between"><span className="text-slate-500 font-medium">Minimum</span><span className="num font-semibold text-slate-600">{fmtDT(minP)}</span></div>}
              {maxP > 0 && <div className="flex justify-between"><span className="text-slate-500 font-medium">Maximum</span><span className="num font-semibold text-slate-600">{fmtDT(maxP)}</span></div>}
            </div>

            <div className="my-4 border-t border-slate-200" />
            <div className="flex items-end justify-between">
              <span className="font-semibold text-slate-700">Total</span>
              <span className="text-2xl font-extrabold text-indigo-600 num" data-testid="cart-total">{fmtDT(total)}</span>
            </div>

            {belowMin && <div className="mt-3 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />Ajoutez encore {fmtDT(minP - total)} pour atteindre le minimum.</div>}
            {aboveMax && <div className="mt-3 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 flex items-start gap-1.5"><Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />Vous dépassez le maximum de {fmtDT(total - maxP)}.</div>}

            {/* Grossiste livreur */}
            <div className="mt-4">
              <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Truck className="h-4 w-4 text-indigo-600" /> Grossiste de livraison <span className="text-rose-500">*</span></label>
              <select data-testid="grossiste-city" value={grossisteCity} onChange={(e) => { setGrossisteCity(e.target.value); setGrossisteId(""); }}
                className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-300 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow">
                <option value="">Toutes gouvernorats ({grossistes.length})</option>
                {[...new Set(grossistes.map((g) => (g.city || "").replace(/^Meddnine$/i, "Médenine")).filter(Boolean))].sort().map((c) => (
                  <option key={c} value={c}>{c} ({grossistes.filter((g) => (g.city || "").replace(/^Meddnine$/i, "Médenine") === c).length})</option>
                ))}
              </select>
              <select data-testid="grossiste-select" value={grossisteId} onChange={(e) => setGrossisteId(e.target.value)}
                className={`mt-2 w-full h-11 px-3 rounded-lg border text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow ${!grossisteId && total > 0 ? "border-amber-300" : "border-slate-300"}`}>
                <option value="">Choisir le grossiste qui vous livre…</option>
                {grossistes.filter((g) => !grossisteCity || (g.city || "").replace(/^Meddnine$/i, "Médenine") === grossisteCity).map((g) => <option key={g.id} value={g.id}>{g.name}{g.city ? ` — ${g.city}` : ""}</option>)}
              </select>
              {total > 0 && !grossisteId && <p className="mt-1.5 text-xs font-medium text-amber-600">Sélectionnez votre grossiste livreur pour valider.</p>}
            </div>

            <button data-testid="validate-participation" onClick={submit} disabled={!canSubmit || submitting}
              className="mt-5 w-full h-12 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CheckCircle2 className="h-5 w-5" /> Valider ma participation</>}
            </button>
            {!isOpen && <p className="mt-3 text-xs text-center text-slate-400 font-medium">Cette opération n'est plus ouverte à la participation.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
